"use client";
import { useState, useCallback } from "react";
import { Input } from "./input"; // Assuming 'input.tsx' is in the same directory or accessible

interface DropzoneProps {
   onFileDrop: (file: File) => void;
   acceptedFileTypes?: string;
   maxFileSize?: number;
   isProcessing?: boolean;
   disabled?: boolean;
   dropzoneText?: string;
   dropzoneSubText?: string;
   className?: string;
}

export const Dropzone: React.FC<DropzoneProps> = ({
   onFileDrop,
   acceptedFileTypes = ".txt,.md,.docx,.pdf",
   maxFileSize = 100 * 1024 * 1024,
   isProcessing = false,
   disabled = false,
   dropzoneText = "Drag & drop a file here, or click to select",
   dropzoneSubText = `Accepted: ${acceptedFileTypes} (Max ${
      maxFileSize / (1024 * 1024)
   }MB)`,
   className = ""
}) => {
   const [isDragging, setIsDragging] = useState(false);
   const [error, setError] = useState<string | null>(null);

   const handleDragOver = useCallback(
      (event: React.DragEvent<HTMLDivElement>) => {
         event.preventDefault();
         event.stopPropagation();
         if (!isProcessing && !disabled) {
            setIsDragging(true);
            setError(null);
         }
      },
      [isProcessing, disabled]
   );

   const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);
   }, []);

   const processFile = useCallback(
      (file: File) => {
         if (file.size > maxFileSize) {
            setError(`File is too large. Max size is ${maxFileSize / (1024 * 1024)}MB.`);
            return;
         }

         const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
         if (
            acceptedFileTypes &&
            !acceptedFileTypes
               .split(",")
               .map((ft) => ft.trim())
               .includes(fileExtension!)
         ) {
            setError(`Invalid file type. Accepted types: ${acceptedFileTypes}`);
            return;
         }
         setError(null);
         onFileDrop(file);
      },
      [onFileDrop, acceptedFileTypes, maxFileSize]
   );

   const handleDrop = useCallback(
      (event: React.DragEvent<HTMLDivElement>) => {
         event.preventDefault();
         event.stopPropagation();
         setIsDragging(false);
         if (
            !isProcessing &&
            !disabled &&
            event.dataTransfer.files &&
            event.dataTransfer.files.length > 0
         ) {
            const droppedFile = event.dataTransfer.files[0];
            processFile(droppedFile);
         }
      },
      [isProcessing, disabled, processFile]
   );

   const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
         const selectedFile = event.target.files[0];
         processFile(selectedFile);
         event.target.value = "";
      }
   };

   const effectiveDropzoneText = isDragging ? "Drop the file here" : dropzoneText;

   return (
      <div className={`flex flex-col space-y-2 ${className}`}>
         <div
            className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-xl space-y-2
                        ${
                           isProcessing || disabled
                              ? "cursor-not-allowed bg-gray-100 dark:bg-gray-800 opacity-70"
                              : "cursor-pointer"
                        }
                        ${
                           isDragging
                              ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                              : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
                        }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() =>
               !isProcessing &&
               !disabled &&
               document.getElementById("dropzoneFileInput")?.click()
            }
         >
            <Input
               id="dropzoneFileInput"
               onChange={handleInputChange}
               type="file"
               accept={acceptedFileTypes}
               className="hidden"
               disabled={isProcessing || disabled}
            />
            <svg
               xmlns="http://www.w3.org/2000/svg"
               fill="none"
               viewBox="0 0 24 24"
               strokeWidth={1.5}
               stroke="currentColor"
               className={`w-10 h-10 ${
                  isDragging ? "text-blue-500" : "text-gray-400 dark:text-gray-500"
               }`}
            >
               <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 16.5V9.75m0 0l-3 3m3-3l3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.338 0 4.5 4.5 0 01-1.41 8.775H6.75z"
               />
            </svg>
            <p
               className={`text-sm text-center ${
                  isDragging
                     ? "text-blue-600 dark:text-blue-300"
                     : "text-gray-600 dark:text-gray-400"
               }`}
            >
               {effectiveDropzoneText}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500">{dropzoneSubText}</p>
         </div>
         {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      </div>
   );
};
